<template>
  <v-container fluid>
    <v-row align="center">
      <v-col class="d-flex flex-row align-center flex-wrap">
        <v-btn v-if="!drillDef" text class="mr-4" @click="$router.go(-1)"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <v-btn v-else text class="mr-4" :to="reportLocation()"><v-icon left>mdi-arrow-left</v-icon>Report</v-btn>
        <div>
          <h1 class="text-h5">
            {{ report ? report.template.name : 'Report' }}
            <span v-if="drillDef" class="grey--text text--darken-1"> / {{ drillDef.name }}</span>
          </h1>
          <div v-if="report" class="grey--text text--darken-1">
            {{ drillDef ? (drillDef.description || report.template.description) : report.template.description }}
          </div>
        </div>
        <v-chip v-if="verification && !drillDef" small class="ml-3" :color="verification.ok ? 'success' : 'warning'" outlined>
          <v-icon left small>{{ verification.ok ? 'mdi-check-decagram' : 'mdi-alert-outline' }}</v-icon>
          {{ verification.ok ? 'Verified' : 'Checks failing' }}
        </v-chip>
        <v-progress-circular indeterminate color="green" v-if="loading" class="ml-3" />
        <v-spacer />

        <!-- underlying data (opens in a new tab with the same filters) -->
        <v-menu v-if="!drillDef && drilldowns.length" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn outlined class="mr-2" v-on="on"><v-icon left>mdi-table-search</v-icon>Underlying data</v-btn>
          </template>
          <v-list dense>
            <v-subheader>Opens in a new tab with these filters</v-subheader>
            <v-list-item v-for="d in drilldowns" :key="d.id" @click="openDrill(d)">
              <v-list-item-content>
                <v-list-item-title>{{ d.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="d.description" style="white-space: normal">{{ d.description }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action><v-icon small>mdi-open-in-new</v-icon></v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>

        <template v-if="result">
          <v-menu offset-y v-if="exportsList.length">
            <template v-slot:activator="{ on }">
              <v-btn outlined class="mr-2" v-on="on"><v-icon left>mdi-download</v-icon>Export</v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="(e, i) in exportsList" :key="i" @click="doExport(e)">
                <v-list-item-title>{{ e.name }} ({{ e.type.toUpperCase() }})</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu offset-y v-if="printsList.length > 1">
            <template v-slot:activator="{ on }">
              <v-btn outlined v-on="on"><v-icon left>mdi-printer</v-icon>Print</v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="(p, i) in printsList" :key="i" @click="doPrint(p)">
                <v-list-item-title>{{ p.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn v-else-if="printsList.length === 1" outlined @click="doPrint(printsList[0])">
            <v-icon left>mdi-printer</v-icon>Print
          </v-btn>
        </template>
      </v-col>
    </v-row>

    <template v-if="report">
      <v-card outlined class="mb-3" v-if="report.template.params.length">
        <v-card-text>
          <rb-filters
            v-model="values" :params="report.template.params" :options="report.paramOptions"
            :loading="running" @run="run"
          />
        </v-card-text>
      </v-card>

      <!-- what the numbers were calculated with (also in exports and prints) -->
      <div v-if="result && result.info && result.info.length" class="mb-3 d-flex flex-wrap align-center">
        <span class="text-caption grey--text text--darken-1 mr-3">Calculated with:</span>
        <v-tooltip v-for="(i, k) in result.info" :key="k" bottom max-width="360">
          <template v-slot:activator="{ on }">
            <v-chip small outlined class="mr-2 mb-1" v-on="on">
              {{ i.label }}: <b class="ml-1">{{ fmt(i.value, i.format) }}</b>
              <v-icon v-if="i.note" x-small right>mdi-information-outline</v-icon>
            </v-chip>
          </template>
          <span>{{ i.note || i.label }}</span>
        </v-tooltip>
      </div>

      <v-alert v-if="error" type="error" outlined>{{ error }}</v-alert>
      <v-alert v-if="!result && !error && !running" type="info" text>Choose the filters above and press Run.</v-alert>

      <rb-report-renderer v-if="result" ref="renderer" :template="report.template" :result="result" :drill="drillId" :details-ctx="ctx" />
      <div v-if="result" class="caption grey--text mt-2">
        Generated {{ new Date(result.meta.generatedAt).toLocaleString() }} in {{ result.meta.durationMs }} ms
      </div>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import RbFilters, { defaultValues } from '../../components/rb/RbFilters.vue'
import RbReportRenderer from '../../components/rb/RbReportRenderer.vue'
import { errorText, runExport, buildPrintHtml, printHtml, formatValue, renderChartImages, printedSections } from '../../components/rb/rbUtil'

const lsKey = (k) => `rb:filters:${k}`
const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)

// ---- filters <-> URL query ----
// range=2026-03-01..2026-03-31 &branchIds=1,2 &includeZero=1 ; an empty value means "explicitly none"
const enc = (s) => String(s).replace(/%/g, '%25').replace(/,/g, '%2C')
const dec = (s) => String(s).replace(/%2C/gi, ',').replace(/%25/g, '%')

export function valuesToQuery (params, values) {
  const q = {}
  for (const p of params) {
    const v = values[p.name]
    if (p.type === 'daterange') q[p.name] = v && (v.from || v.to) ? `${v.from || ''}..${v.to || ''}` : ''
    else if (Array.isArray(v)) q[p.name] = v.map(enc).join(',')
    else if (p.type === 'boolean') q[p.name] = v === null || v === undefined ? '' : (v ? '1' : '0')
    else q[p.name] = isEmpty(v) ? '' : String(v)
  }
  return q
}

export function queryToValues (params, query, base) {
  const out = { ...base }
  for (const p of params) {
    if (!(p.name in query)) continue
    const raw = Array.isArray(query[p.name]) ? query[p.name][0] : query[p.name]
    const s = raw === null || raw === undefined ? '' : String(raw)
    if (p.type === 'daterange') {
      const [from, to] = s.split('..')
      out[p.name] = { from: from || '', to: to || '' }
    } else if (p.multiple) {
      const items = s === '' ? [] : s.split(',').map(dec)
      out[p.name] = p.type === 'number' ? items.map(Number) : items
    } else if (p.type === 'boolean') {
      out[p.name] = s === '' ? null : s === '1'
    } else if (p.type === 'number') {
      out[p.name] = s === '' ? null : Number(s)
    } else {
      out[p.name] = s === '' ? null : s
    }
  }
  return out
}

export default {
  name: 'ReportsHubRun',
  components: { RbFilters, RbReportRenderer },
  data () {
    return { loading: false, running: false, report: null, values: {}, result: null, error: '' }
  },
  computed: {
    ...mapGetters(['getEndpoint', 'getUser']),
    drillId () { return this.$route.params.drill || null },
    drilldowns () { return (this.report && this.report.template.drilldowns) || [] },
    drillDef () { return this.drillId ? this.drilldowns.find((d) => d.id === this.drillId) || null : null },
    verification () { return this.report && this.report.verification },
    exportsList () {
      if (!this.report) return []
      if (!this.drillDef) return this.report.template.exports || []
      if (this.drillDef.exports && this.drillDef.exports.length) return this.drillDef.exports
      return this.drillDef.datasets.flatMap((d) => [{ name: d.name, type: 'csv', dataset: d.name }, { name: d.name, type: 'xlsx', dataset: d.name }])
    },
    printsList () {
      if (!this.report) return []
      const list = this.drillDef ? this.drillDef.prints : this.report.template.prints
      return list && list.length ? list : [{ name: 'Standard', paper: 'A4', orientation: this.drillDef ? 'landscape' : 'portrait' }]
    },
    userName () {
      const u = this.getUser || {}
      return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || ''
    }
  },
  async mounted () {
    try {
      this.loading = true
      const res = await axios.get(`${this.getEndpoint}/api/rb-viewer/${encodeURIComponent(this.$route.params.key)}`)
      this.report = res.data.data
      this.applyRoute()
      // reports with a required filter that has no value (e.g. "pick a session") wait for the user
      if (this.missingRequired().length === 0) await this.run()
    } finally {
      this.loading = false
    }
  },
  watch: {
    // report <-> its data view reuse this component: reload for the other view
    async drillId () {
      if (!this.report) return
      this.result = null
      this.error = ''
      this.applyRoute()
      if (this.missingRequired().length === 0) await this.run()
    },
    // browser back/forward (or a pasted link) changes the filters: follow the URL
    '$route.query' () {
      if (!this.report || this.running) return
      const params = this.report.template.params
      const current = valuesToQuery(params, this.values)
      const incoming = valuesToQuery(params, queryToValues(params, this.$route.query, this.values))
      if (JSON.stringify(current) !== JSON.stringify(incoming)) {
        this.applyRoute()
        if (this.missingRequired().length === 0) this.run()
      }
    }
  },
  methods: {
    fmt: formatValue,
    // defaults <- last-used filters (when the URL carries none) <- URL
    applyRoute () {
      const params = this.report.template.params
      let values = defaultValues(params)
      const urlHasFilters = params.some((p) => p.name in this.$route.query)
      if (!urlHasFilters) {
        try {
          const saved = JSON.parse(localStorage.getItem(lsKey(this.report.key)) || 'null')
          if (saved && typeof saved === 'object') values = { ...values, ...saved }
        } catch (e) { /* storage unavailable: fall back to defaults */ }
      }
      this.values = queryToValues(params, this.$route.query, values)
    },
    missingRequired () {
      return this.report.template.params.filter((p) => {
        if (!p.required) return false
        const v = this.values[p.name]
        return p.type === 'daterange' ? isEmpty(v && v.from) || isEmpty(v && v.to) : isEmpty(v)
      })
    },
    filterQuery () { return valuesToQuery(this.report.template.params, this.values) },
    reportLocation () {
      return { name: 'Report View', params: { key: this.$route.params.key }, query: this.report ? this.filterQuery() : {} }
    },
    openDrill (d) {
      const href = this.$router.resolve({
        name: 'Report Data', params: { key: this.report.key, drill: d.id }, query: this.filterQuery()
      }).href
      window.open(href, '_blank')
    },
    async run () {
      const missing = this.missingRequired()
      if (missing.length) {
        this.error = `Please choose: ${missing.map((p) => p.label || p.name).join(', ')}.`
        return
      }
      try {
        this.running = true
        this.error = ''
        const res = await axios.post(`${this.getEndpoint}/api/rb-viewer/${encodeURIComponent(this.report.key)}/run`, {
          params: this.values, drill: this.drillId
        })
        this.result = res.data.data
        // remember the filters: in the URL (back/forward, bookmarks, new tabs) and as this report's last-used set
        if (this.report.template.urlFilters !== false) {
          const query = this.filterQuery()
          if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
            try { await this.$router.replace({ query }) } catch (e) { /* redundant navigation */ }
          }
          try { localStorage.setItem(lsKey(this.report.key), JSON.stringify(this.values)) } catch (e) { /* ignore */ }
        }
      } catch (e) {
        this.result = null
        this.error = errorText(e)
      } finally {
        this.running = false
      }
    },
    ctx () {
      return {
        template: this.report.template, drill: this.drillId, result: this.result, values: this.values,
        options: this.report.paramOptions, user: this.userName, reportKey: this.report.key, version: this.report.version
      }
    },
    doExport (e) {
      runExport(e, this.ctx())
    },
    doPrint (p) {
      const c = this.ctx()
      const all = this.drillDef ? this.drillDef.ui.sections : this.report.template.ui.sections
      printHtml(buildPrintHtml(c, p, renderChartImages(printedSections(all, p), this.result)))
    }
  }
}
</script>
